import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import LinkIcon from "@material-ui/icons/Link";
import BlockIcon from "@material-ui/icons/Block";
import PeopleIcon from "@material-ui/icons/People";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import PersonIcon from "@material-ui/icons/Person";
import RefreshIcon from "@material-ui/icons/Refresh";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 1000,
    maxWidth: 1400,
    minHeight: 600,
  },
  dialogTitle: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
  },
  closeButton: {
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(0.5),
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  tabContent: {
    padding: theme.spacing(2),
  },
  statsCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(2),
    textAlign: "center",
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
  },
  statsCardHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
  },
  statsNumber: {
    fontSize: "2rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(0.5),
  },
  statsLabel: {
    fontSize: "0.9rem",
    opacity: 0.9,
  },
  statsRate: {
    fontSize: "0.8rem",
    opacity: 0.8,
    marginTop: theme.spacing(0.5),
  },
  tableContainer: {
    maxHeight: 500,
    marginTop: theme.spacing(2),
  },
  statusChip: {
    minWidth: 80,
    fontSize: "0.75rem",
  },
  viewedChip: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  acknowledgedChip: {
    backgroundColor: "#2196f3",
    color: "white",
  },
  dismissedChip: {
    backgroundColor: "#f44336",
    color: "white",
  },
  linkClickedChip: {
    backgroundColor: "#ff9800",
    color: "white",
  },
  notViewedChip: {
    backgroundColor: "#9e9e9e",
    color: "white",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 200,
  },
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`stats-tabpanel-${index}`}
      aria-labelledby={`stats-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const AnnouncementStatsModal = ({ open, onClose, announcementId }) => {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (open && announcementId) {
      fetchStats();
    }
  }, [open, announcementId]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/announcement-views/${announcementId}/stats`);
      setStats(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: ptBR });
  };

  const getUserStatus = (user) => {
    if (user.hasViewed) {
      return { label: "Visualizado", style: { backgroundColor: "#4caf50", color: "white" } };
    }
    return { label: "Pendente", style: { backgroundColor: "#9e9e9e", color: "white" } };
  };

  const getUserAcknowledgmentStatus = (user) => {
    if (!user.hasViewed) {
      return { label: "Pendente", style: { backgroundColor: "#9e9e9e", color: "white" } };
    }

    // 🎯 Lógica diferente baseada na configuração do anúncio
    if (stats?.announcement.requiresAcknowledge) {
      // Quando REQUER confirmação: "Li e Entendi" vs "Ver depois"
      if (user.hasAcknowledged) {
        return { label: "Li e Entendi", style: { backgroundColor: "#4caf50", color: "white" } };
      }
      return { label: "Ver depois", style: { backgroundColor: "#ff9800", color: "white" } };
    } else {
      // Quando NÃO requer confirmação: "Não mostrar mais" vs "Fechou" 
      if (user.dismissedForever) {
        return { label: "Não mostrar mais", style: { backgroundColor: "#ff9800", color: "white" } };
      }
      return { label: "Fechou", style: { backgroundColor: "#2196f3", color: "white" } };
    }
  };

  if (!stats && !loading) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <div style={{ paddingRight: 48 }}>
          <Typography variant="h6">
            📊 Estatísticas do Anúncio
          </Typography>
          {stats && (
            <Typography variant="body1" style={{ opacity: 0.95, fontWeight: 500 }}>
              {stats.announcement.title}
            </Typography>
          )}
        </div>
        <IconButton
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab label="📈 Estatísticas Gerais" />
        <Tab label="👥 Por Usuário" />
      </Tabs>

      <DialogContent>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <TabPanel value={tabValue} index={0}>
              <div className={classes.tabContent}>
                <Grid container spacing={3}>
                  {/* Linha 1 - Métricas principais */}
                  <Grid item xs={12} sm={4}>
                    <Card className={classes.statsCard}>
                      <div className={classes.statsCardHeader}>
                        <VisibilityIcon style={{ fontSize: "2rem" }} />
                        <Typography className={classes.statsNumber}>
                          {stats?.generalStats.totalViews || 0}
                        </Typography>
                      </div>
                      <Typography className={classes.statsLabel}>
                        Total Visualizações
                      </Typography>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <Card className={classes.statsCard}>
                      <div className={classes.statsCardHeader}>
                        <PeopleIcon style={{ fontSize: "2rem" }} />
                        <Typography className={classes.statsNumber}>
                          {stats?.generalStats.uniqueViewers || 0}
                        </Typography>
                      </div>
                      <Typography className={classes.statsLabel}>
                        Usuários Únicos
                      </Typography>
                      <Typography className={classes.statsRate}>
                        {stats?.generalStats.reachRate || 0}% de alcance
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Card className={classes.statsCard}>
                      <div className={classes.statsCardHeader}>
                        <CheckCircleIcon style={{ fontSize: "2rem" }} />
                        <Typography className={classes.statsNumber}>
                          {stats?.generalStats.totalAcknowledgments || 0}
                        </Typography>
                      </div>
                      <Typography className={classes.statsLabel}>
                        Confirmações
                      </Typography>
                      <Typography className={classes.statsRate}>
                        {stats?.generalStats.acknowledgmentRate || 0}% confirmaram
                      </Typography>
                    </Card>
                  </Grid>

                  {/* Linha 2 - Métricas secundárias */}
                  <Grid item xs={12} sm={4}>
                    <Card className={classes.statsCard}>
                      <div className={classes.statsCardHeader}>
                        <LinkIcon style={{ fontSize: "2rem" }} />
                        <Typography className={classes.statsNumber}>
                          {stats?.generalStats.totalLinkClicks || 0}
                        </Typography>
                      </div>
                      <Typography className={classes.statsLabel}>
                        Cliques em Links
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Card className={classes.statsCard}>
                      <div className={classes.statsCardHeader}>
                        <BlockIcon style={{ fontSize: "2rem" }} />
                        <Typography className={classes.statsNumber}>
                          {stats?.generalStats.totalDismissalsForever || 0}
                        </Typography>
                      </div>
                      <Typography className={classes.statsLabel}>
                        Dispensas Permanentes
                      </Typography>
                      <Typography className={classes.statsRate}>
                        {stats?.generalStats.dismissalRate || 0}% dispensaram
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Card className={classes.statsCard}>
                      <div className={classes.statsCardHeader}>
                        <TrendingUpIcon style={{ fontSize: "2rem" }} />
                        <Typography className={classes.statsNumber}>
                          {stats?.generalStats.totalViewLater || 0}
                        </Typography>
                      </div>
                      <Typography className={classes.statsLabel}>
                        "Ver depois"
                      </Typography>
                      <Typography className={classes.statsRate}>
                        {stats?.generalStats.viewLaterRate || 0}% escolheram esperar
                      </Typography>
                    </Card>
                  </Grid>

                  {/* Segunda fileira de cards */}
                  <Grid item xs={12} sm={4}>
                    <Card className={classes.statsCard}>
                      <div className={classes.statsCardHeader}>
                        <PersonIcon style={{ fontSize: "2rem", opacity: 0.7 }} />
                        <Typography className={classes.statsNumber}>
                          {stats?.generalStats.usersNotViewed || 0}
                        </Typography>
                      </div>
                      <Typography className={classes.statsLabel}>
                        Não Acessaram
                      </Typography>
                      <Typography className={classes.statsRate}>
                        {((stats?.generalStats.usersNotViewed || 0) / Math.max(stats?.generalStats.totalUsers || 1, 1) * 100).toFixed(1)}% não visualizaram
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Card className={classes.statsCard}>
                      <div className={classes.statsCardHeader}>
                        <TrendingUpIcon style={{ fontSize: "2rem" }} />
                        <Typography className={classes.statsNumber}>
                          {stats?.generalStats.engagementRate || 0}%
                        </Typography>
                      </div>
                      <Typography className={classes.statsLabel}>
                        Taxa de Engajamento
                      </Typography>
                      <Typography className={classes.statsRate}>
                        Usuários que interagiram
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Card className={classes.statsCard}>
                      <div className={classes.statsCardHeader}>
                        <RefreshIcon style={{ fontSize: "2rem" }} />
                        <Typography className={classes.statsNumber}>
                          {stats?.generalStats.multipleViewsCount || 0}
                        </Typography>
                      </div>
                      <Typography className={classes.statsLabel}>
                        Múltiplas Visualizações
                      </Typography>
                      <Typography className={classes.statsRate}>
                        Usuários que viram mais de 1x
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
              </div>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <div className={classes.tabContent}>
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Nome</TableCell>
                        {/* Mostrar coluna empresa se há usuários de empresas diferentes (anúncio global) */}
                        {stats?.userStats.some(user => user.companyName) && (
                          <TableCell>Empresa</TableCell>
                        )}
                        <TableCell align="center">Status</TableCell>
                        {/* Coluna Interação sempre aparece quando há dados */}
                        <TableCell align="center">Interação</TableCell>
                        {/* Só mostrar coluna Link se o anúncio tem link externo */}
                        {stats?.announcement.externalUrl && (
                          <TableCell align="center">Link</TableCell>
                        )}
                        <TableCell align="center">Exibições</TableCell>
                        <TableCell>Última Atividade</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats?.userStats.map((user) => {
                        const status = getUserStatus(user);
                        const acknowledgmentStatus = getUserAcknowledgmentStatus(user);
                        
                        return (
                          <TableRow key={user.id}>
                            <TableCell>
                              <div>
                                <Typography variant="body2" style={{ fontWeight: 500 }}>
                                  {user.name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {user.email}
                                </Typography>
                              </div>
                            </TableCell>
                            {/* Mostrar empresa se há usuários de empresas diferentes (anúncio global) */}
                            {stats?.userStats.some(u => u.companyName) && (
                              <TableCell>
                                <Typography variant="body2">
                                  {user.companyName || `Empresa ${user.companyId}`}
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell align="center">
                              <Chip
                                label={status.label}
                                size="small"
                                style={status.style}
                              />
                            </TableCell>
                            {/* Coluna Interação sempre aparece */}
                            <TableCell align="center">
                              <Chip
                                label={acknowledgmentStatus.label}
                                size="small"
                                style={acknowledgmentStatus.style}
                              />
                            </TableCell>
                            {/* Só mostrar coluna Link se o anúncio tem link externo */}
                            {stats?.announcement.externalUrl && (
                              <TableCell align="center">
                                {user.hasClickedLink ? (
                                  <Chip
                                    label="Aberto"
                                    size="small"
                                    style={{ backgroundColor: "#4caf50", color: "white" }}
                                  />
                                ) : (
                                  <Chip
                                    label="-"
                                    size="small"
                                    style={{ backgroundColor: "#9e9e9e", color: "white" }}
                                  />
                                )}
                              </TableCell>
                            )}
                            <TableCell align="center">
                              {user.displayCount || 0}x
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(user.lastDisplayAt || user.acknowledgedAt || user.viewedAt)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AnnouncementStatsModal;
